import type {
  ClientJoinedData,
  SystemMessage,
  ClientServerRequest,
  ReceivedMessage,
  SystemParams,
  SharedApiRegistry,
  ApiType,
  AssetChange,
} from "@gotti/core";
import { System } from "@gotti/core";
import type { PeerAreaData } from "./ServerProcess.js";
import type { LeaveChannelReason } from "./constants.js";
import type { ServerAssetsFacade } from "./assets/assetsFacade.js";

/**
 * Open interface for server-side API registration via declaration merging.
 *
 * Systems augment this interface to declare the API methods they provide.
 * TypeScript merges all augmentations into a single type automatically.
 *
 * @example
 * ```typescript
 * declare module '@gotti/server' {
 *   interface ServerApiRegistry {
 *     getPlayer: (id: string) => ServerPlayer;
 *     allPlayers: () => ServerPlayer[];
 *   }
 * }
 *
 * export class PlayerManagerSystem extends ServerSystem<ServerPlayerManagerApi>() {
 *   onInit() {
 *     this.addApi('getPlayer', (id) => this.players[id]);
 *   }
 * }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ServerApiRegistry {}

/**
 * A message a server system sends to client(s). With `to`, it is delivered to
 * that CLIENT SYSTEM's `onServerMessage`; without it, to the client game/area
 * file's message hook. `T` types `data` (`this.dispatchToClient<HealthUpdate>(…)`).
 * The one type behind `dispatchToClient` / `dispatchToClients` /
 * `dispatchToAllClients` on systems, `ServerProcess` and `AreaServer`.
 */
export type ClientBoundMessage<T = unknown> = {
  type: string | number;
  data: T;
  to?: string | number;
};

/**
 * Interface for server-side system capabilities.
 *
 * Two patterns:
 * 1. **No-arg generic**: `extends ServerSystem<MyApi>()` — standalone server system
 *    with typed `addApi()` constraints.
 * 2. **Shared inheritance**: `extends ServerSystem(SharedBase)` — for systems
 *    that share logic between client and server via a common base class.
 *
 * All dispatch methods (`dispatchToAllClients`, `dispatchToClient`, etc.) are injected
 * at runtime by `ServerProcess.decorate()` + `bindServerMethods()`.  **Writing a method
 * body for any dispatch method will throw a runtime error.**
 *
 * @example
 * ```typescript
 * import { ServerSystem } from '@gotti/server';
 *
 * export class MyServerSystem extends ServerSystem<MyApi>() {
 *   // this.dispatchToAllClients() just works — no declares needed!
 *   // this.addApi() is constrained to keys of MyApi
 * }
 * ```
 */
export interface IServerSystem {
  // ── Identity flag ──────────────────────────────────────────────────────
  readonly __is_server: true;

  // ── Typed $api ─────────────────────────────────────────────────────────
  /** Full merged server + shared API registry. */
  $api: ServerApiRegistry & SharedApiRegistry;

  // ── Runtime-injected properties (set by ServerProcess.onAddedSystem) ──
  readonly meta: {
    areaId: string;
    areaType: string;
    gameId: string;
    gameType: string;
    areaConfig?: any;
    gameConfig?: any;
  };
  $gameAreas: { [id: string]: PeerAreaData };
  $areaData: any;
  /** Namespaced read/write asset accessor; injected by `ServerProcess.onAddedSystem`. */
  $assets: ServerAssetsFacade;

  // ── Instance state ─────────────────────────────────────────────────────
  readonly requestCbs: Record<string, (responseData: any, error?: string) => void>;

  // ── Dispatch methods (bound by ServerProcess via bindServerMethods) ────
  requestClient: (clientId: string, data: SystemMessage, timeout?: number) => Promise<any>;
  dispatchToAreas: (areaIds: string[], data: SystemMessage) => void;
  dispatchToArea: (areaId: string, data: SystemMessage) => void;
  dispatchToMaster: (message: { type: string | number; data: any }) => void;
  requestMaster: (message: { type: string | number; data: any }, timeout?: number) => Promise<any>;
  /**
   * Create a dynamic area from this area (e.g. a matchmaker spinning up a match
   * world). Returns the new area id; this area owns it (only it or a master may
   * `destroyArea` it). Explicit create — a duplicate `(type, key)` throws.
   */
  createArea: (type: string, opts?: { key?: string, gameId?: string, configData?: any, managed?: boolean, maxClients?: number, emptyTtlMs?: number }) => Promise<{ areaId: string }>;
  /** Destroy a dynamic area this area created. */
  destroyArea: (areaId: string, opts?: { drain?: boolean, whenEmpty?: boolean, timeoutMs?: number, reason?: string }) => Promise<void>;
  requestArea: (areaId: string, data: SystemMessage) => Promise<any>;
  addClientToArea: (clientId: string, areaId: string | number, opts?: any) => boolean;
  replaceClientArea: (clientId: string, areaId: string | number, opts?: any) => boolean;
  removeClientFromArea: (clientId: string, opts?: any) => boolean;
  dispatchToClient: <T = unknown>(clientId: string | number, message: ClientBoundMessage<T>) => void;
  dispatchToClients: <T = unknown>(clientIds: string[], message: ClientBoundMessage<T>) => void;
  dispatchToAllClients: <T = unknown>(message: ClientBoundMessage<T>) => void;
  dispatchRaw: (data: ArrayBuffer) => void;
  createChannel: (channelName: string, clientIds?: string[]) => void;
  destroyChannel: (channelName: string) => void;
  addClientToChannel: (clientId: string, channelName: string) => void;
  removeClientFromChannel: (clientId: string, channelName: string) => void;
  dispatchToChannel: (channelName: string, message: SystemMessage) => void;
  requestChannel: (channelName: string, message: SystemMessage) => Promise<Array<{ clientId: string; success: boolean; response: any; error?: string }>>;
  getClientsInChannel: (channelName: string) => Array<string>;
  setState<T>(state: Partial<T>): void;

  // ── Lifecycle hooks (optional — implement the ones you need) ───────────
  onGlobalAreaAdded?(areaId: string, areaType: string | number, areaConfig?: any): void;
  onGlobalAreaRemoved?(areaId: string, areaType: string | number, areaConfig?: any): void;
  onGameAreaRemoved?(areaId: string, areaType: string | number, areaConfig?: any): void;
  onGameAreaAdded?(areaId: string, areaType: string | number, areaConfig?: any): void;
  onClientJoined?(data: ClientJoinedData): void;
  onClientLeft?(clientId: string, options: any): void;
  onClientRequest?(clientId: string, request: ClientServerRequest): Promise<any>;
  onGlobalMessage?(message: ReceivedMessage): void;
  onGlobalRequest?(message: ReceivedMessage): Promise<any>;
  onClientMessage?(clientId: string, message: SystemMessage): void;
  onClientLeftChannel?(clientId: string, channelName: string, reason: LeaveChannelReason): void;
  onClientJoinedChannel?(clientId: string, channelName: string): void;
  onChannelCreated?(channelName: string, clientIds?: string[]): void;
  onChannelDestroyed?(channelName: string): void;
  onAreaRequest?(areaId: string, request: ReceivedMessage): Promise<any>;
  onAreaMessage?(areaId: string, message: ReceivedMessage): void;
  onRawClientMessage?(clientId: string | number, data: ArrayBuffer): void;
  /** Fired when an asset visible to this process is created or overwritten. */
  onAssetUpdated?(change: AssetChange): void;
  /** Fired when an asset visible to this process is deleted. */
  onAssetRemoved?(change: AssetChange): void;
}

/**
 * Type guard: checks whether a system has been decorated with server capabilities.
 */
export function isServerSystem(system: unknown): system is IServerSystem {
  return typeof system === 'object' && system !== null && '__is_server' in system;
}

/**
 * Type-level helper that produces a class whose instances are typed as
 * `System & IServerSystem`.
 *
 * **Zero runtime cost** — returns the base class (or `System`) unchanged.
 * All dispatch methods are injected at runtime by `ServerProcess.decorate()`
 * + `bindServerMethods()`.
 *
 * ### Usage patterns
 *
 * **No-arg (recommended)** — pass generics for typed `addApi()`:
 * ```typescript
 * export class MySystem extends ServerSystem<MyApi>() { }
 * ```
 *
 * **With base class** — when sharing a base between client & server:
 * ```typescript
 * export class MySystem extends ServerSystem(SharedBase) { }
 * ```
 *
 * **With base class + explicit API type** — when a subclass registers APIs
 * beyond what the base declares, pass a phantom `api<>()` second argument:
 * ```typescript
 * import { api } from '@gotti/server';
 * type FullApi = BaseApi & { extraMethod: () => void };
 * export class MySystem extends ServerSystem(SharedBase, api<FullApi>()) { }
 * ```
 */
export function ServerSystem<TProvides, TBase extends abstract new (...args: any[]) => System>(
  Base: TBase, _apiType: ApiType<TProvides>,
): new (...args: any[]) => InstanceType<TBase> & System<TProvides> & IServerSystem;
export function ServerSystem<TBase extends abstract new (...args: any[]) => System>(
  Base: TBase,
): new (...args: any[]) => InstanceType<TBase> & IServerSystem;
export function ServerSystem<TProvides = {}, TComponent = {}>():
  (new (name?: string | number | SystemParams) => System<TProvides, TComponent> & IServerSystem);
export function ServerSystem(Base?: any, _apiType?: any) {
  return (Base ?? System) as any;
}
